import React, { useEffect, useState, useContext } from "react";
import {
  Box,
  Breadcrumbs,
  Button,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Card,
  CardContent,
  CardMedia,
  IconButton,
  Tooltip,
  Alert,
} from "@mui/material";
import { emphasize, styled } from "@mui/material/styles";
import HomeIcon from "@mui/icons-material/Home";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { IoSearch } from "react-icons/io5";
import { FaShoppingCart, FaCheckCircle } from "react-icons/fa";
import { MdAddCircle, MdRemoveCircle } from "react-icons/md";
import { fetchDataFromApi } from "../../utils/api";
import { MyContext } from "../../App";

const StyledBreadcrumb = styled(Chip)(({ theme }) => {
  const backgroundColor =
    theme.palette.mode === "light"
      ? theme.palette.grey[100]
      : theme.palette.grey[800];
  return {
    backgroundColor,
    height: theme.spacing(3),
    color: theme.palette.text.primary,
    fontWeight: theme.typography.fontWeightRegular,
    "&:hover, &:focus": {
      backgroundColor: emphasize(backgroundColor, 0.06),
    },
    "&:active": {
      boxShadow: theme.shadows[1],
      backgroundColor: emphasize(backgroundColor, 0.12),
    },
  };
});

const ProductSearch = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);

  const [searchQuery, setSearchQuery] = useState("");
  const [brandFilter, setBrandFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");

  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);

  const context = useContext(MyContext);

  useEffect(() => {
    fetchCategories();
    fetchBrands();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await fetchDataFromApi("/api/category");
      if (response) {
        setCategories(response.categoryList || response);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const fetchBrands = async () => {
    try {
      const response = await fetchDataFromApi("/api/homeOfferBanner/filters/brands");
      if (response) {
        setBrands(response);
      }
    } catch (error) {
      console.error("Error fetching brands:", error);
    }
  };

  const searchProducts = async () => {
    if (!searchQuery && !brandFilter && !categoryFilter && !minPrice && !maxPrice) {
      context.setAlertBox({
        open: true,
        error: true,
        msg: "Please enter at least one search criteria",
      });
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams({
        ...(searchQuery && { search: searchQuery }),
        ...(brandFilter && { brand: brandFilter }),
        ...(categoryFilter && { category: categoryFilter }),
        ...(minPrice && { minPrice: minPrice }),
        ...(maxPrice && { maxPrice: maxPrice }),
        limit: "50",
      });

      const response = await fetchDataFromApi(
        `/api/homeOfferBanner/products/search?${params.toString()}`
      );

      if (response) {
        setProducts(response);
      }
    } catch (error) {
      console.error("Error searching products:", error);
      context.setAlertBox({
        open: true,
        error: true,
        msg: "Failed to search products",
      });
    } finally {
      setLoading(false);
    }
  };

  const resetFilters = () => {
    setSearchQuery("");
    setBrandFilter("");
    setCategoryFilter("");
    setMinPrice("");
    setMaxPrice("");
    setProducts([]);
  };

  const toggleProductSelection = (product) => {
    setSelectedProducts((prev) => {
      const isSelected = prev.some((p) => p._id === product._id);
      if (isSelected) {
        return prev.filter((p) => p._id !== product._id);
      } else {
        return [...prev, product];
      }
    });
  };

  const isProductSelected = (productId) => {
    return selectedProducts.some((p) => p._id === productId);
  };

  const clearSelection = () => {
    setSelectedProducts([]);
  };

  return (
    <div className="right-content w-100">
      <div className="card shadow border-0 w-100 flex-row p-4 align-items-center">
        <h5 className="mb-0">Product Search</h5>

        <Breadcrumbs aria-label="breadcrumb" className="ml-auto breadcrumbs_">
          <StyledBreadcrumb
            component="a"
            href="/"
            label="Dashboard"
            icon={<HomeIcon fontSize="small" />}
          />
          <StyledBreadcrumb
            component="a"
            href="/homeOfferBanners"
            label="Offer Banners"
          />
          <StyledBreadcrumb label="Product Search" deleteIcon={<ExpandMoreIcon />} />
        </Breadcrumbs>
      </div>

      <Box className="card shadow border-0 p-3 mt-4">
        <Typography variant="h6" className="mb-3">
          Search & Filter Products
        </Typography>

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Search Products"
              placeholder="Name, description, brand..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && searchProducts()}
              InputProps={{
                startAdornment: <IoSearch className="mr-2" />,
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Brand"
              value={brandFilter}
              onChange={(e) => setBrandFilter(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Category</InputLabel>
              <Select
                value={categoryFilter}
                label="Category"
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {categories.map((cat) => (
                  <MenuItem key={cat._id} value={cat._id}>
                    {cat.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6} sm={3} md={2}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Min Price"
              value={minPrice}
              onChange={(e) => setMinPrice(e.target.value)}
              inputProps={{ min: 0 }}
            />
          </Grid>

          <Grid item xs={6} sm={3} md={2}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Max Price"
              value={maxPrice}
              onChange={(e) => setMaxPrice(e.target.value)}
              inputProps={{ min: 0 }}
            />
          </Grid>
        </Grid>

        <Box className="mt-3 d-flex gap-2">
          <Button 
            variant="contained" 
            onClick={searchProducts} 
            startIcon={<IoSearch />}
            disabled={loading}
          >
            {loading ? "Searching..." : "Search"}
          </Button>
          <Button variant="outlined" onClick={resetFilters}>
            Reset
          </Button>
        </Box>
      </Box>

      {selectedProducts.length > 0 && (
        <Box className="card shadow border-0 p-3 mt-3">
          <Box className="d-flex justify-content-between align-items-center">
            <Typography variant="body1" color="primary">
              <FaShoppingCart className="mr-2" style={{ verticalAlign: "middle" }} />
              {selectedProducts.length} product(s) selected
            </Typography>
            <Button 
              variant="outlined" 
              color="error" 
              size="small"
              onClick={clearSelection}
            >
              Clear Selection
            </Button>
          </Box>
          
          <Alert severity="info" className="mt-2">
            Use these selected products when creating an offer banner
          </Alert>
        </Box>
      )}

      <Box className="mt-4">
        {loading ? (
          <Box className="text-center p-5">
            <Typography>Searching products...</Typography>
          </Box>
        ) : products.length === 0 ? (
          <Box className="text-center p-5">
            <IoSearch size={60} color="#ccc" />
            <Typography variant="h6" className="mt-3">
              No products found
            </Typography>
            <Typography color="textSecondary">
              {searchQuery || brandFilter || categoryFilter || minPrice || maxPrice
                ? "Try adjusting your search filters"
                : "Enter search criteria to find products"}
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {products.map((product) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={product._id}>
                <Card
                  className={`product-search-card ${
                    isProductSelected(product._id) ? "selected" : ""
                  }`}
                  sx={{
                    border: isProductSelected(product._id)
                      ? "2px solid #1976d2"
                      : "1px solid #e0e0e0",
                    cursor: "pointer",
                  }}
                  onClick={() => toggleProductSelection(product)}
                >
                  {isProductSelected(product._id) && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 10,
                        right: 10,
                        zIndex: 1,
                        backgroundColor: "white",
                        borderRadius: "50%",
                      }}
                    >
                      <FaCheckCircle size={24} color="#1976d2" />
                    </Box>
                  )}

                  <CardMedia
                    component="img"
                    height="180"
                    image={product.images?.[0] || "/placeholder.jpg"}
                    alt={product.name}
                    sx={{ objectFit: "cover" }}
                  />

                  <CardContent>
                    <Typography variant="h6" className="product-name" gutterBottom>
                      {product.name}
                    </Typography>

                    {product.brand && (
                      <Typography variant="body2" color="textSecondary" className="mb-1">
                        Brand: {product.brand}
                      </Typography>
                    )}

                    <Box className="d-flex justify-content-between align-items-center mb-2">
                      <Typography variant="h6" color="primary">
                        ₹{product.price}
                      </Typography>
                      {product.discount > 0 && (
                        <Chip
                          label={`${product.discount}% OFF`}
                          color="error"
                          size="small"
                        />
                      )}
                    </Box>

                    <Button
                      fullWidth
                      variant={isProductSelected(product._id) ? "contained" : "outlined"}
                      color={isProductSelected(product._id) ? "error" : "primary"}
                      startIcon={
                        isProductSelected(product._id) ? <MdRemoveCircle /> : <MdAddCircle />
                      }
                      sx={{ mt: 1 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleProductSelection(product);
                      }}
                    >
                      {isProductSelected(product._id) ? "Remove" : "Add"}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    </div>
  );
};

export default ProductSearch;
